import React, { useState, useEffect } from 'react';
import { StudySession, FlashcardSet } from '../types';
import { mockFlashcardSets } from '../data/mockData';
import FlashcardDisplay from './FlashcardDisplay';
import ActionButtons from './ActionButtons';
import StudySummary from './StudySummary';
import TopicSelection from './TopicSelection';
import TopicHeader from './TopicHeader';
import { ProGate } from './ProGate';
import { getFlashcardSetAccess } from '../catalog/flashcards';
import { useStreak } from '../contexts/StreakContext';
import { useAuth } from '../AuthContext';
import { autoCompleteTaskFromContent, markTaskCompleted } from '../services/missionTaskCompletion';
import {
  saveProgress,
  loadProgress
} from '../utils/firebaseStorage';

import { MissionBanner } from './common/MissionBanner';
import { Check, X } from 'lucide-react';

interface FlashcardModuleProps {
  className?: string;
  preSelectedTopicId?: string | null;
  resetToTopics?: boolean;
  onNavigate?: (section: string) => void;
}

const FlashcardModule: React.FC<FlashcardModuleProps> = ({ className = '', preSelectedTopicId, resetToTopics, onNavigate }) => {
  const { trackActivity } = useStreak();
  const { currentUser } = useAuth();

  const [session, setSession] = useState<StudySession>({
    selectedTopic: null,
    cards: [],
    currentIndex: 0,
    knownCards: [],
    unknownCards: [],
    isComplete: false,
    isRevisionMode: false,
  });

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isFlipped, setIsFlipped] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [transitionDirection, setTransitionDirection] = useState<'known' | 'unknown' | null>(null);

  // Mission Banner State
  const [showMissionBanner, setShowMissionBanner] = useState(false);
  const [missionBannerData, setMissionBannerData] = useState<any>(null);

  useEffect(() => {
    // Check for pending mission
    const pendingMission = localStorage.getItem('pendingMission');
    if (pendingMission) {
      try {
        const missionData = JSON.parse(pendingMission);
        const dismissed = JSON.parse(localStorage.getItem('dismissedMissionKeys') || '{}');

        // Show if valid, not dismissed, and is ZSV module (or generic)
        if (missionData &&
          missionData.createdAt &&
          (Date.now() - missionData.createdAt < 24 * 60 * 60 * 1000) &&
          !dismissed[missionData.missionKey || ''] &&
          (missionData.module === 'zsv' || !missionData.module)
        ) {
          setMissionBannerData(missionData);
          setShowMissionBanner(true);
        }
      } catch (e) {
        console.error("Failed to parse pendingMission:", e);
      }
    }

    // Deep Linking: Check URL params for set/topic
    const params = new URLSearchParams(window.location.search);
    const setParam = params.get('set') || params.get('topic');
    if (setParam) {
      // Need to find the set object from mockFlashcardSets
      // This requires mockFlashcardSets to be imported/available.
      // It is imported at top.
      const set = mockFlashcardSets.find(s => s.id === setParam);
      if (set) {
        handleTopicSelect(set);
      }
    }
  }, []);

  useEffect(() => {
    // Initialize session - start with topic selection or pre-selected topic
    const initializeSession = () => {
      try {
        // Firebase storage is now ready for cross-device sync

        // Validate flashcard sets data
        if (!mockFlashcardSets || mockFlashcardSets.length === 0) {
          throw new Error('Žádná témata nejsou k dispozici');
        }

        // Validate flashcard sets structure
        const invalidSets = mockFlashcardSets.filter(set =>
          !set.id || !set.title || !set.cards || set.cards.length === 0
        );

        if (invalidSets.length > 0) {
          throw new Error('Některá témata obsahují neplatná data');
        }

        // If a topic is pre-selected, automatically select it
        if (preSelectedTopicId) {
          const selectedSet = mockFlashcardSets.find(set => set.id === preSelectedTopicId);
          if (selectedSet) {
            console.log(`Auto-selecting pre-selected topic: ${selectedSet.title}`);
            handleTopicSelect(selectedSet);
            setIsLoading(false);
            return;
          } else {
            console.warn(`Pre-selected topic ID "${preSelectedTopicId}" not found, showing topic selection`);
          }
        }

        // Start with no topic selected - show topic selection
        setSession({
          selectedTopic: null,
          cards: [],
          currentIndex: 0,
          knownCards: [],
          unknownCards: [],
          isComplete: false,
          isRevisionMode: false,
        });
        setError(null);
        setIsLoading(false);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Nastala neočekávaná chyba');
        setIsLoading(false);
      }
    };

    // Simulate loading delay
    setTimeout(initializeSession, 500);
  }, [preSelectedTopicId]);

  // Save progress when component unmounts or user navigates away
  useEffect(() => {
    return () => {
      // Save current progress before component unmounts
      if (session.selectedTopic && (session.knownCards.length > 0 || session.unknownCards.length > 0)) {
        saveSessionProgress(session);
      }
    };
  }, [session]);

  // Auto-save progress periodically and when currentIndex changes
  useEffect(() => {
    if (session.selectedTopic && session.currentIndex > 0) {
      // Save progress when user moves through cards (even without answering)
      const timeoutId = setTimeout(() => {
        saveSessionProgress(session);
      }, 2000); // Save after 2 seconds of inactivity

      return () => clearTimeout(timeoutId);
    }
  }, [session.currentIndex, session.selectedTopic, session]);

  // Reset to topics when resetToTopics prop changes
  useEffect(() => {
    if (resetToTopics) {
      handleBackToTopics();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetToTopics]);

  const handleFlip = () => {
    setIsFlipped(!isFlipped);
  };

  const moveToNextCard = (direction: 'known' | 'unknown') => {
    if (isTransitioning) return;

    setIsTransitioning(true);
    setTransitionDirection(direction);

    // Add current card to appropriate array based on direction
    const currentCardId = session.cards[session.currentIndex].id;
    const updatedKnownCards = direction === 'known'
      ? [...session.knownCards, currentCardId]
      : session.knownCards;
    const updatedUnknownCards = direction === 'unknown'
      ? [...session.unknownCards, currentCardId]
      : session.unknownCards;

    // Track activity for streak system (pass number of cards studied)
    const totalCardsStudied = updatedKnownCards.length + updatedUnknownCards.length;
    console.log('🃏 Flashcard Check:', { totalCardsStudied, currentIndex: session.currentIndex });

    if (totalCardsStudied > 0 && totalCardsStudied % 5 === 0) {
      console.log('✅ Reached 5 cards threshold, calling trackActivity...');
      trackActivity('flashcard_reviewed', totalCardsStudied);
    } else {
      console.log('ℹ️ Did not reach threshold or multiple of 5:', totalCardsStudied);
    }

    // Start transition animation
    setTimeout(() => {
      if (session.currentIndex < session.cards.length - 1) {
        const updatedSession = {
          ...session,
          currentIndex: session.currentIndex + 1,
          knownCards: updatedKnownCards,
          unknownCards: updatedUnknownCards
        };
        setSession(updatedSession);

        // Save progress after moving to next card
        saveSessionProgress(updatedSession);

        setIsFlipped(false); // Reset flip state for next card
      } else {
        // Session completed
        const completedSession = {
          ...session,
          currentIndex: session.currentIndex + 1,
          knownCards: updatedKnownCards,
          unknownCards: updatedUnknownCards,
          isComplete: true
        };
        setSession(completedSession);

        // Save completion status to localStorage
        saveSessionProgress(completedSession);

        // Track activity for streak system (pass number of cards studied)
        const totalCardsStudied = updatedKnownCards.length + updatedUnknownCards.length;
        console.log('🃏 Flashcard Session Complete/Check:', { totalCardsStudied, isComplete: true });

        if (totalCardsStudied > 0 && totalCardsStudied % 5 === 0) {
          trackActivity('flashcard_reviewed', totalCardsStudied);
        }

        // Auto-complete mission task if user is logged in
        if (currentUser && session.selectedTopic) {
          autoCompleteTaskFromContent(
            currentUser.uid,
            'flashcards',
            session.selectedTopic.id
          ).then(() => {
            window.dispatchEvent(new Event('taskCompleted'));
          });
        }
      }

      // Complete transition
      setTimeout(() => {
        setIsTransitioning(false);
        setTransitionDirection(null);
      }, 150);
    }, 150);
  };

  // Save progress to localStorage
  const saveSessionProgress = (updatedSession: StudySession) => {
    if (!updatedSession.selectedTopic) return;

    const progress = {
      currentIndex: updatedSession.currentIndex,
      knownCards: updatedSession.knownCards,
      unknownCards: updatedSession.unknownCards,
      isCompleted: updatedSession.isComplete,
      lastStudied: new Date().toISOString(),
      totalCards: updatedSession.selectedTopic.cards.length
    };

    saveProgress(updatedSession.selectedTopic.id, progress).catch(error =>
      console.error('Failed to save flashcard progress:', error)
    );
  };

  const handleKnown = () => {
    if (isTransitioning) return;

    moveToNextCard('known');
  };

  const handleUnknown = () => {
    if (isTransitioning) return;

    moveToNextCard('unknown');
  };

  const handleTopicSelect = async (selectedSet: FlashcardSet) => {
    try {
      // Validate selected flashcard set
      if (!selectedSet) {
        throw new Error('Neplatné téma bylo vybráno');
      }

      if (!selectedSet.id || !selectedSet.title) {
        throw new Error('Vybrané téma obsahuje neplatná data');
      }

      if (!selectedSet.cards || selectedSet.cards.length === 0) {
        throw new Error('Vybrané téma neobsahuje žádné flashkarty');
      }

      // Validate flashcard structure
      const invalidCards = selectedSet.cards.filter(card =>
        !card.id || !card.question || !card.answer
      );

      if (invalidCards.length > 0) {
        throw new Error('Některé flashkarty v tomto tématu obsahují neplatná data');
      }

      // Load saved progress from Firebase
      const savedProgress = await loadProgress(selectedSet.id);

      let sessionState: StudySession;

      if (savedProgress && !savedProgress.isCompleted) {
        // Resume from saved progress
        console.log(`Resuming topic: ${selectedSet.title} from card ${savedProgress.currentIndex + 1}/${savedProgress.totalCards}`);

        sessionState = {
          selectedTopic: selectedSet,
          cards: selectedSet.cards,
          currentIndex: Math.min(savedProgress.currentIndex, selectedSet.cards.length - 1), // Ensure index is valid
          knownCards: savedProgress.knownCards,
          unknownCards: savedProgress.unknownCards,
          isComplete: savedProgress.isCompleted,
          isRevisionMode: false,
        };
      } else if (savedProgress && savedProgress.isCompleted) {
        // Topic was completed, but allow restart or revision
        console.log(`Topic ${selectedSet.title} was previously completed. Starting fresh session.`);

        sessionState = {
          selectedTopic: selectedSet,
          cards: selectedSet.cards,
          currentIndex: 0,
          knownCards: [],
          unknownCards: [],
          isComplete: false,
          isRevisionMode: false,
        };
      } else {
        // No saved progress, start fresh
        console.log(`Starting new session with topic: ${selectedSet.title} (${selectedSet.cards.length} cards)`);

        sessionState = {
          selectedTopic: selectedSet,
          cards: selectedSet.cards,
          currentIndex: 0,
          knownCards: [],
          unknownCards: [],
          isComplete: false,
          isRevisionMode: false,
        };
      }

      setSession(sessionState);

      // Reset UI state
      setIsFlipped(false);
      setIsTransitioning(false);
      setTransitionDirection(null);
      setError(null);

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Chyba při načítání tématu');
      console.error('Error selecting topic:', err);
    }
  };

  const handleBackToTopics = () => {
    // Track activity before leaving (if user studied any cards)
    const totalCardsStudied = session.knownCards.length + session.unknownCards.length;
    if (totalCardsStudied > 0) {
      console.log(`🏃‍♂️ User leaving session. Tracking ${totalCardsStudied} flashcards.`);
      trackActivity('flashcard_reviewed', totalCardsStudied);
    }

    // Save current progress before leaving
    if (session.selectedTopic && (session.knownCards.length > 0 || session.unknownCards.length > 0)) {
      saveSessionProgress(session);
    }

    // Reset session state completely
    setSession({
      selectedTopic: null,
      cards: [],
      currentIndex: 0,
      knownCards: [],
      unknownCards: [],
      isComplete: false,
      isRevisionMode: false,
    });

    // Reset UI state
    setIsFlipped(false);
    setIsTransitioning(false);
    setTransitionDirection(null);
    setError(null);

    console.log('Returned to topic selection');
  };

  const handleStartRevision = async () => {
    try {
      // Get saved progress to access the original unknown cards
      const savedProgress = session.selectedTopic ? await loadProgress(session.selectedTopic.id) : null;
      const unknownCardIds = new Set(savedProgress?.unknownCards || session.unknownCards);
      const revisionCards = session.selectedTopic?.cards.filter(card => unknownCardIds.has(card.id)) || [];

      // Handle edge case where no unknown cards exist
      if (revisionCards.length === 0) {
        setError('Žádné karty k opakování nejsou k dispozici');
        return;
      }

      const revisionSession = {
        ...session,
        cards: revisionCards,
        currentIndex: 0,
        knownCards: [],
        unknownCards: [],
        isComplete: false,
        isRevisionMode: true,
      };

      setSession(revisionSession);

      setIsFlipped(false);
      setError(null); // Clear any previous errors

      console.log(`Starting revision with ${revisionCards.length} unknown cards`);
    } catch (err) {
      setError('Chyba při spuštění opakování');
    }
  };

  if (isLoading) {
    return (
      <div className={`flex items-center justify-center min-h-96 ${className}`}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Načítám flashkarty...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`flex items-center justify-center min-h-96 ${className}`}>
        <div className="text-center max-w-md mx-auto">
          <div className="text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
            Chyba při načítání
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            {error}
          </p>
          <button
            onClick={() => window.location.reload()}
            className="
              inline-flex items-center justify-center
              px-6 py-3 
              bg-blue-600 hover:bg-blue-700 
              dark:bg-blue-500 dark:hover:bg-blue-600
              text-white font-semibold rounded-lg
              transition-colors duration-200
              focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
              dark:focus:ring-offset-gray-800
            "
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Zkusit znovu
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`h-full flex flex-col ${className}`}>
      <div className="flex-1 w-full">
        {/* Mission Banner */}
        <MissionBanner
          visible={showMissionBanner && !!missionBannerData}
          label={missionBannerData?.label || "Dnešní mise"}
          focus={missionBannerData?.focus || "zsv"}
          minutes={missionBannerData?.minutes || 15}
          module="zsv"
          onStart={() => setShowMissionBanner(false)}
          onDismiss={() => {
            if (missionBannerData?.missionKey) {
              const dismissed = JSON.parse(localStorage.getItem('dismissedMissionKeys') || '{}');
              dismissed[missionBannerData.missionKey] = true;
              localStorage.setItem('dismissedMissionKeys', JSON.stringify(dismissed));
            }
            setShowMissionBanner(false);
          }}
          onComplete={() => {
            if (missionBannerData?.taskId) {
              markTaskCompleted(missionBannerData.taskId);
            }
            setShowMissionBanner(false);
          }}
          className="mb-6"
        />

        {/* Topic Selection Screen */}
        {!session.selectedTopic && (
          <TopicSelection
            flashcardSets={mockFlashcardSets}
            onTopicSelect={handleTopicSelect}
            onNavigate={onNavigate}
          />
        )}

        {/* Flashcard Study Screen */}
        {session.selectedTopic && (
          <ProGate access={getFlashcardSetAccess(session.selectedTopic.id)} source="flashcards">
            <div className="w-full overflow-x-hidden">
              <div className="w-full max-w-5xl mx-auto">
                {/* Topic Header with Back Button */}
                <TopicHeader
                  selectedTopic={session.selectedTopic}
                  isRevisionMode={session.isRevisionMode}
                  onBackToTopics={handleBackToTopics}
                />

                {/* Progress Header - Quizlet style */}
                {session.cards.length > 0 && !session.isComplete && (
                  <div className="flex justify-between items-center mb-6 px-1 sm:px-2">
                    {/* Unknown Count (Left) */}
                    <div className="flex items-center justify-center gap-1.5 sm:gap-2 text-red-600 dark:text-red-400 font-bold px-3 sm:px-4 py-2 rounded-xl border-2 border-foreground/10 dark:border-white/10">
                      <X className="w-5 h-5 sm:w-6 sm:h-6" strokeWidth={3} />
                      <span className="text-lg sm:text-xl">{session.unknownCards.length}</span>
                    </div>

                    {/* Current/Total (Center) */}
                    <div className="flex items-center justify-center font-bold text-base sm:text-lg text-gray-600 dark:text-white">
                      {session.currentIndex + 1} / {session.cards.length}
                    </div>

                    {/* Known Count (Right) */}
                    <div className="flex items-center justify-center gap-1.5 sm:gap-2 text-green-600 dark:text-green-400 font-bold px-3 sm:px-4 py-2 rounded-xl border-2 border-foreground/10 dark:border-white/10">
                      <span className="text-lg sm:text-xl">{session.knownCards.length}</span>
                      <Check className="w-5 h-5 sm:w-6 sm:h-6" strokeWidth={3} />
                    </div>
                  </div>
                )}

                {/* Flashcard Display with Enhanced Transition Animation */}
                {session.cards.length > 0 && !session.isComplete && (
                  <>
                    <div className="relative w-full overflow-hidden">
                      <div
                        className={`
                        w-full transition-all duration-300 ease-in-out transform
                        ${isTransitioning && transitionDirection === 'known'
                            ? 'animate-slide-out-right'
                            : isTransitioning && transitionDirection === 'unknown'
                              ? 'animate-slide-out-left'
                              : 'animate-fade-in-up'
                          }
                      `}
                        key={`card-${session.currentIndex}`}
                      >
                        <FlashcardDisplay
                          card={session.cards[session.currentIndex]}
                          isFlipped={isFlipped}
                          onFlip={handleFlip}
                          onKnown={handleKnown}
                          onUnknown={handleUnknown}
                        />
                      </div>
                    </div>

                    {/* Action Buttons - always rendered and visible */}
                    <div className={`
                    transition-all duration-200 ease-in-out
                    ${(isTransitioning) ? 'pointer-events-none' : 'pointer-events-auto'}
                    opacity-100
                  `}>
                      <ActionButtons
                        onKnown={handleKnown}
                        onUnknown={handleUnknown}
                        disabled={isTransitioning}
                      />
                    </div>
                  </>
                )}

                {/* Study Summary */}
                {session.isComplete && (
                  <StudySummary
                    knownCount={session.knownCards.length}
                    unknownCount={session.unknownCards.length}
                    onStartRevision={handleStartRevision}
                    selectedTopic={session.selectedTopic}
                    onBackToTopics={handleBackToTopics}
                  />
                )}


              </div>
            </div>
          </ProGate>
        )}
      </div>
    </div>
  );
};

export default FlashcardModule;